import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { LoggingApi } from './loggingApi';
import {
  LoggingSearchRequest,
  LoggingSearchResponse,
  LoggingIndicesResponse,
} from './type';

export type Options = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  proxyPath?: string;
  cacheDuration?: number; // in milliseconds
};

const DEFAULT_PROXY_PATH = '/logging';

class Client {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly proxyPath: string;
  private readonly cache = new Map<
    string,
    { timestamp: number; response: any }
  >();
  private readonly CACHE_DURATION: number;

  constructor(opts: Options) {
    this.discoveryApi = opts.discoveryApi;
    this.fetchApi = opts.fetchApi;
    this.proxyPath = opts.proxyPath ?? DEFAULT_PROXY_PATH;
    this.CACHE_DURATION = opts.cacheDuration ?? 5 * 60 * 1000; // Default 5 minutes
  }

  private generateCacheKey(prefix: string, data?: any): string {
    if (!data) {
      return prefix;
    }
    const sortedData = JSON.stringify(data, Object.keys(data).sort());
    return `${prefix}:${sortedData}`;
  }

  private async fetchWithCache<T>(
    key: string,
    fetcher: () => Promise<T>,
  ): Promise<T> {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.response as T;
    }
    const response = await fetcher();
    this.cache.set(key, { timestamp: Date.now(), response });
    return response;
  }

  public invalidateCache(pattern?: string): void {
    if (!pattern) {
      // Clear all cache
      this.cache.clear();
      return;
    }

    // Invalidate cache entries matching the pattern
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  public async fetch<T = any>(input: string, init?: RequestInit): Promise<T> {
    const apiUrl = await this.apiUrl();
    const resp = await this.fetchApi.fetch(`${apiUrl}${input}`, init);
    if (!resp.ok) {
      throw new Error(`Request failed with ${resp.status} ${resp.statusText}`);
    }

    return await resp.json();
  }

  async searchLogs(
    indexName: string,
    searchRequest: LoggingSearchRequest,
  ): Promise<LoggingSearchResponse> {
    const encodedIndexName = encodeURIComponent(indexName);
    const response = await this.fetch<LoggingSearchResponse>(
      `/${encodedIndexName}/_search`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchRequest),
      },
    );

    return response;
  }

  async getIndices(): Promise<string[]> {
    const cacheKey = this.generateCacheKey('getIndices');

    return this.fetchWithCache(cacheKey, async () => {
      const response = await this.fetch<LoggingIndicesResponse>('/_aliases', {
        method: 'GET',
      });

      const indexNames = Object.keys(response);
      const patterns = new Set<string>();

      indexNames.forEach(indexName => {
        // Remove date patterns like -2025.10.13, -2025-10-13, -20251013, etc. And change to wildcard -*
        const pattern = indexName.replace(
          /-?\d{4}[-.]?\d{2}[-.]?\d{2}$/g,
          '-*',
        );
        patterns.add(pattern);
      });

      // Convert Set to Array and sort
      return Array.from(patterns).sort();
    });
  }

  private async apiUrl() {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return proxyUrl + this.proxyPath;
  }
}

export class LoggingApiClient implements LoggingApi {
  private readonly client: Client;

  constructor(opts: Options) {
    this.client = new Client(opts);
  }

  async searchLogs(
    indexName: string,
    searchRequest: LoggingSearchRequest,
  ): Promise<LoggingSearchResponse> {
    return this.client.searchLogs(indexName, searchRequest);
  }

  async getIndices(): Promise<string[]> {
    return this.client.getIndices();
  }

  invalidateCache(pattern?: string): void {
    this.client.invalidateCache(pattern);
  }

  getCacheStats(): { size: number; keys: string[] } {
    return this.client.getCacheStats();
  }
}
